import React, { useEffect, useMemo } from "react";
import { PaymentDetail } from "@features/cx/payments/types";
import { DynamicForm } from "@shared/components/DynamicForm";
import { FormField, FormService } from "@shared/services/FormServices";
import { ValueLabel } from "@features/ax/shared/ValueLabel";
import { Badge } from "@shared/ui/badge";
import {
  useCurrency,
  useRemoteUtilityId,
} from "@shared/selectors/globalSelectors";
import { BUTTON_FIELD_CLASSES } from "@shared/utils/constants";
import { toast } from "@shared/hooks/use-toast";
import {
  usePaymentChoices,
  useUserNames,
  useCheckRefundPayment,
  usePaymentMode,
} from "../../../../hooks";

interface DetailsStepProps {
  payment: any;
  refundData: any;
  setRefundData: (data: any) => void;
  onCancel: () => void;
  onNext?: () => void; // Provided by Stepper
}

export const DetailsStep: React.FC<DetailsStepProps> = ({
  payment,
  refundData,
  setRefundData,
  onCancel,
  onNext,
}) => {
  const currency = useCurrency();
  const remoteUtilityId = useRemoteUtilityId();

  const paymentId = payment?.id || payment?.paymentId;
  const { data: checkData } = useCheckRefundPayment(
    paymentId,
    Number(remoteUtilityId),
  );

  const { data: paymentModesData } = usePaymentMode(Number(remoteUtilityId));
  const paymentModeList = Array.isArray(paymentModesData)
    ? paymentModesData
    : Array.isArray(paymentModesData?.result)
      ? paymentModesData.result
      : [];

  const dynamicRefundMethodOptions = [
    { label: "Back to Original Method", value: "Back to Original Method" },
    ...paymentModeList.map((m: any) => ({
      label: m.name,
      value: m.name,
    })),
  ];

  const dynamicCustomFields: FormField[] = paymentModeList.flatMap((m: any) => {
    const fields = m.customFields || [];
    return fields.map((cf: any, index: number) => {
      const isLastOdd = fields.length % 2 !== 0 && index === fields.length - 1;
      const isAmountField =
        cf.key === "amount" || cf.label?.toLowerCase().includes("amount");

      return {
        name: cf.key || cf.id?.toString(),
        label: cf.label,
        type: cf.fieldType === "date" ? "datepicker" : cf.fieldType || "text",
        required: true,
        disabled: (values: any) =>
          isAmountField && values.refundType === "Full Refund",
        placeholder:
          cf.placeholder ||
          (cf.fieldType === "date"
            ? "Select date"
            : `Enter ${cf.label?.toLowerCase()}`),
        showWhen: (values: any) =>
          values.refundMethod === m.name &&
          (values.disposition === "Refund to Customer" || !values.disposition),
        className: isLastOdd ? "col-span-12" : "col-span-12 md:col-span-6",
      };
    });
  });

  const refundableAmount = checkData?.refundableAmount
    ? parseFloat(checkData.refundableAmount)
    : 0;

  const { data: refundTypeChoicesData } = usePaymentChoices("refund_types");
  const { data: dispositionsData } = usePaymentChoices("dispositions");
  const { data: refundReasonsData } = usePaymentChoices("refund_reasons");

  const refundTypeOptions =
    refundTypeChoicesData?.result?.map((item: any) => ({
      label: item.value,
      value: item.value,
      description:
        item.value === "Full Refund"
          ? "Return the entire original amount."
          : "Return part of the original amount.",
    })) || [];

  const dispositionOptions =
    dispositionsData?.result?.map((item: any) => ({
      label: item.value,
      value: item.value,
      description:
        item.value === "Create Credit Note"
          ? "Retain as credit balance. A credit note will be generated — no cash returned."
          : "Return the amount to the customer via a payment method.",
    })) || [];

  const refundReasonOptions =
    refundReasonsData?.result?.map((item: any) => ({
      label: item.value,
      value: String(item.key),
    })) || [];

  const { data: usersData, isLoading: loadingUsers } = useUserNames(
    Number(remoteUtilityId),
  );

  const supervisorOptions = useMemo(
    () =>
      usersData?.map((data: any) => ({
        value: data.name,
        label: data.name,
      })) || [],
    [usersData],
  );

  const fields: FormField[] = [
    {
      name: "refundType",
      label: "Refund Type",
      type: "radio-card",
      defaultValue: "Full Refund",
      required: true,
      className: "col-span-12",
      options: refundTypeOptions,
      classes: {
        container: "mb-2",
        radioGroup: "grid grid-cols-1 md:grid-cols-2 gap-4 w-full",
        radioCard:
          "flex items-start gap-3 p-4 rounded-lg border-2 cursor-pointer transition-all hover:border-red-300 bg-white w-full",
        radioCardChecked:
          "border-red-500 bg-red-50/70 shadow-sm ring-1 ring-red-500",
        label: "font-bold text-gray-900 text-sm leading-tight",
        description: "text-xs text-gray-500 mt-1 font-medium leading-normal",
      },
    },
    {
      name: "disposition",
      label: "Disposition",
      type: "radio-card",
      defaultValue: "Refund to Customer",
      required: true,
      className: "col-span-12",
      options: dispositionOptions,
      classes: {
        container: "mb-2",
        radioGroup: "grid grid-cols-1 md:grid-cols-2 gap-4 w-full",
        radioCard:
          "flex items-start gap-3 p-4 rounded-lg border-2 cursor-pointer transition-all hover:border-red-300 bg-white w-full",
        radioCardChecked:
          "border-red-500 bg-red-50/70 shadow-sm ring-1 ring-red-500",
        label: "font-bold text-gray-900 text-sm leading-tight",
        description: "text-xs text-gray-500 mt-1 font-medium leading-normal",
      },
    },
    {
      name: "refundAmount",
      label: "Refund Amount",
      type: "number",
      required: true,
      prefix: currency,
      disabled: (values: any) =>
        values.refundType === "Full Refund" || !values.refundType,
      helperText: `Max refundable: ${currency} ${refundableAmount.toFixed(2)}`,
      className: "col-span-12 md:col-span-6",
    },
    {
      name: "refundReason",
      label: "Refund Reason",
      type: "select",
      required: true,
      options: refundReasonOptions,
      placeholder: "Select a reason",
      className: "col-span-12 md:col-span-6",
    },
    {
      name: "refundMethod",
      label: "Refund Method",
      type: "select",
      required: true,
      showWhen: (values) =>
        values.disposition === "Refund to Customer" || !values.disposition,
      options: dynamicRefundMethodOptions,
      placeholder: "Select refund method",
      className: "col-span-12",
    },
    ...dynamicCustomFields,
    {
      name: "approver",
      label: "Approver",
      type: "select",
      required: true,
      options: supervisorOptions,
      placeholder: "Select approver",
      className: "col-span-12",
    },
    {
      name: "remarks",
      label: "Remarks",
      type: "textarea",
      required: false,
      placeholder: "Any notes for audit trail",
      className: "col-span-12",
    },
  ];

  const form = FormService.createForm({
    fields,
    defaultValues: refundData || {
      refundType: "Full Refund",
      disposition: "Refund to Customer",
      fullRefundAmount: refundableAmount.toFixed(2),
      refundAmount: refundableAmount,
      refundMethod: "Back to Original Method",
    },
  });

  useEffect(() => {
    if (refundData) {
      form.reset(refundData);
    }
  }, [refundData, form]);

  const watchRefundType = form.watch("refundType");
  const watchRefundAmount = form.watch("refundAmount");

  useEffect(() => {
    if (watchRefundType === "Full Refund") {
      form.setValue("refundAmount", refundableAmount, { shouldValidate: true });
      paymentModeList.forEach((m: any) => {
        m.customFields?.forEach((cf: any) => {
          const isAmountField =
            cf.key === "amount" || cf.label?.toLowerCase().includes("amount");
          if (isAmountField) {
            form.setValue(cf.key || cf.id?.toString(), refundableAmount, {
              shouldValidate: true,
            });
          }
        });
      });
    } else if (watchRefundType === "Partial Refund") {
      paymentModeList.forEach((m: any) => {
        m.customFields?.forEach((cf: any) => {
          const isAmountField =
            cf.key === "amount" || cf.label?.toLowerCase().includes("amount");
          if (isAmountField) {
            form.setValue(cf.key || cf.id?.toString(), "", {
              shouldValidate: false,
            });
          }
        });
      });
    }
  }, [watchRefundType, refundableAmount, form, paymentModeList]);

  const isPartial = watchRefundType === "Partial Refund";
  const parsedRefundAmount = parseFloat(watchRefundAmount);
  const amountExceeds =
    isPartial &&
    !isNaN(parsedRefundAmount) &&
    parsedRefundAmount > refundableAmount;

  const amountIsZeroOrLess =
    isPartial && !isNaN(parsedRefundAmount) && parsedRefundAmount <= 0;

  const displayFields = fields.map((field) => {
    if (field.name === "refundAmount") {
      let helperText = `Max refundable: ${currency} ${refundableAmount.toFixed(2)}`;
      let isError = false;

      if (amountExceeds) {
        helperText = `Amount exceeds refundable limit of ${currency} ${refundableAmount.toFixed(2)}`;
        isError = true;
      } else if (amountIsZeroOrLess) {
        helperText = "Enter a valid refund amount.";
        isError = true;
      }

      return {
        ...field,
        helperText,
        classes: isError
          ? { helperText: "text-red-500 font-medium text-sm" }
          : undefined,
      };
    }
    return field;
  });

  const handleSubmit = (data: any) => {
    let finalAmount = refundableAmount;
    if (data.refundType === "Partial Refund") {
      const amount = parseFloat(data.refundAmount);
      if (isNaN(amount) || amount <= 0) {
        toast({
          title: "Error",
          description: "Enter a valid refund amount.",
          variant: "destructive",
        });
        return;
      }
      if (amount > refundableAmount) {
        toast({
          title: "Error",
          description: `Refund amount exceeds refundable balance (${currency} ${refundableAmount.toFixed(2)}).`,
          variant: "destructive",
        });
        return;
      }
      finalAmount = amount;
    }

    setRefundData({
      ...data,
      finalAmount,
      refundableAmount,
    });

    if (onNext) {
      onNext();
    }
  };

  return (
    <div className="space-y-6">
      <div className="border border-slate-200 rounded-xl p-5 bg-slate-50/30">
        <div className="flex justify-between items-start mb-2">
          <div>
            <h4 className="text-lg font-bold text-slate-800">
              {checkData?.consumerName ||
                `${payment.consumerFirstName || ""} ${payment.consumerLastName || ""}`}
            </h4>
            <p className="text-[13px] text-slate-500 mt-1">
              Account{" "}
              {checkData?.consumerNo ||
                payment.consumerNo ||
                payment.consumerId}{" "}
              - Receipt {checkData?.receiptNo || payment.receiptNo || "N/A"}
            </p>
          </div>
          <Badge
            variant="outline"
            className="border-cyan-200 bg-cyan-50/50 text-cyan-600 rounded-full px-3 py-0.5 text-[13px] font-medium"
          >
            {checkData?.paymentTypeLabel || payment.paymentPayType || "N/A"}
          </Badge>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-6">
          <ValueLabel
            label="Original Amount"
            value={
              <span className="text-slate-800 font-bold">
                {currency}{" "}
                {checkData?.originalAmount || payment.amount || "0.00"}
              </span>
            }
          />
          <ValueLabel
            label="Method"
            value={
              <span className="text-slate-800 font-bold">
                {payment.paymentModeDisplay || "N/A"}
              </span>
            }
          />
          <ValueLabel
            label="Date"
            value={
              <span className="text-slate-800 font-bold">
                {checkData?.paymentDate || payment.paymentDate || "N/A"}
              </span>
            }
          />
          <ValueLabel
            label="Refundable"
            value={
              <span className="text-red-600 font-bold">
                {currency} {refundableAmount.toFixed(2)}
              </span>
            }
          />
        </div>
      </div>

      <DynamicForm
        form={form}
        fields={displayFields}
        onSubmit={handleSubmit}
        onReset={onCancel}
        loading={false}
        gridColumns={12}
        responsiveColumns={{ sm: 12, md: 12 }}
        config={{
          layout: "vertical",
          showSubmitButton: true,
          submitButtonText: "Continue to Review",
          showResetButton: true,
          resetButtonText: "Cancel",
          classes: BUTTON_FIELD_CLASSES,
        }}
      />
    </div>
  );
};
